'use client';

import {
  useContext,
  useEffect,
  useState,
  type FC,
  Dispatch,
  SetStateAction,
} from 'react';
import {
  DeleteQuestionnairesDialog,
  Dropdown,
  EditDueDateDialog,
  EditRemindersDialog,
  Button,
  EditTemplateReminderDialog,
  ResultListItem,
  EditTagsDialog,
  RestoreQuestionnaireDialog,
} from '@/_components';
import { SelectFirmInternalStatusDialog } from './_components/SelectFirmInternalStatusDialog';
import { downloadOverviewPageAsSpreadsheet } from '@/_api/advisors';
import { lockQuestionnaires, sendReminders } from '@/_api/admin-questionnaires';
import type { QuestionnaireIndexSerializer } from '@/_types/serializers/v1/questionnaires';
import type { OverviewFilterObject } from '@/_types/overview';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';

export const MultiSelectActions: FC<{
  isLoading: boolean;
  loadQuestionnaires: () => void;
  selectedCount: number;
  selectedQuestionnaires: QuestionnaireIndexSerializer[];
  setSelectedQuestionnaires: Dispatch<
    SetStateAction<Array<QuestionnaireIndexSerializer>>
  >;
  filter?: OverviewFilterObject;
}> = ({
  isLoading: isLoadingFromProps,
  loadQuestionnaires,
  selectedCount,
  selectedQuestionnaires,
  setSelectedQuestionnaires,
  filter,
}) => {
  const { addToast } = useContext(ToastContext);
  const { token } = useContext(UserContext);

  const [isLoading, setIsLoading] = useState(isLoadingFromProps || false);

  const [isSendRemindersDialogOpened, setIsSendRemindersDialogOpened] =
    useState(false);
  const [isEditDueDateDialogOpened, setIsEditDueDateDialogOpened] =
    useState(false);
  const [isEditRemindersDialogOpened, setIsEditRemindersDialogOpened] =
    useState(false);
  const [isEditEntityTagsDialogOpened, setIsEditEntityTagsDialogOpened] =
    useState(false);
  const [
    isDeleteQuestionnairesDialogOpened,
    setIsDeleteQuestionnairesDialogOpened,
  ] = useState(false);
  const [
    isChangeFirmInternalStatusDialogOpened,
    setIsChangeFirmInternalStatusDialogOpened,
  ] = useState(false);
  const [isRestoreDialogOpened, setIsRestoreDialogOpened] = useState(false);

  useEffect(() => {
    setIsLoading(isLoadingFromProps || false);
  }, [isLoadingFromProps]);

  const onLockQuestionnaires = async () => {
    try {
      setIsLoading(true);

      await lockQuestionnaires({
        questionnaireIds: selectedQuestionnaires.map((q) => q.id),
        token,
      });

      await loadQuestionnaires();

      addToast('success', 'Questionnaire(s) successfully locked');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onDownloadOverviewPage = async () => {
    try {
      setIsLoading(true);

      await downloadOverviewPageAsSpreadsheet({
        questionnaireIds: selectedQuestionnaires.map((q) => q.id),
        token,
      });

      addToast(
        'success',
        'Your spreadsheet will be available shortly in your notifications.',
      );
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const onSendNow = async () => {
    try {
      setIsLoading(true);

      const baseParams = {
        questionnaire_ids: selectedQuestionnaires.map((q) => q.id),
      };

      const response = await sendReminders({
        params: baseParams,
        token,
      });

      if (response.error) {
        addToast('error', response.error);
        return;
      }

      addToast('success', 'Reminders sent successfully');
    } catch (error) {
      console.error(error);
      addToast('error', null, { error });
    } finally {
      setIsLoading(false);
    }
  };

  const unlockedQuestionnaires = selectedQuestionnaires?.filter(
    (q) => !q.locked,
  );

  const reminderEnabledQuestionnaires = selectedQuestionnaires?.filter((q) => {
    return (
      !q.reminders_disabled &&
      !q.locked &&
      !q.deleted_at &&
      !q.completed &&
      !q.is_draft
    );
  });

  // Get unique entity IDs from selected questionnaires
  const uniqueEntityIds = Array.from(
    new Set(selectedQuestionnaires.map((q) => q.company_id)),
  );

  const items: Array<ResultListItem> = filter?.show_only_deleted
    ? [
        {
          name: 'Restore Selected Questionnaires',
          label: `${selectedCount}`,
          labelType: 'info',
          onClick: () => setIsRestoreDialogOpened(true),
        },
      ]
    : ([
        unlockedQuestionnaires.length && {
          name: 'Lock Questionnaires',
          label: `${unlockedQuestionnaires.length}`,
          labelType: 'info',
          onClick: onLockQuestionnaires,
        },
        {
          name: 'Change Firm-Internal Status for Questionnaires',
          label: `${selectedCount}`,
          labelType: 'info',
          onClick: () => setIsChangeFirmInternalStatusDialogOpened(true),
        },
        {
          name: 'Download Overview Page for Questionnaires',
          label: `${selectedCount}`,
          labelType: 'info',
          onClick: onDownloadOverviewPage,
        },
        {
          name: 'Delete Questionnaires',
          label: `${selectedCount}`,
          labelType: 'info',
          onClick: () => setIsDeleteQuestionnairesDialogOpened(true),
        },
        { type: 'separator' },
        {
          name: 'Edit Tags for Unique Associated Entities',
          label: `${uniqueEntityIds.length}`,
          labelType: 'info',
          onClick: () => setIsEditEntityTagsDialogOpened(true),
        },
        { type: 'separator' },
        {
          name: 'Edit Due Dates',
          label: `${selectedCount}`,
          labelType: 'info',
          onClick: () => setIsEditDueDateDialogOpened(true),
        },
        {
          name: 'Reminders',
          items: [
            {
              name: 'Send Reminders',
              label: `${reminderEnabledQuestionnaires.length}`,
              labelType: 'info',
              isHidden: !reminderEnabledQuestionnaires.length,
              onClick: onSendNow,
            },
            {
              name: 'Edit Reminder Messages',
              label: `${selectedCount}`,
              labelType: 'info',
              onClick: () => setIsSendRemindersDialogOpened(true),
            },
            {
              name: 'Edit Reminder Status',
              label: `${selectedCount}`,
              labelType: 'info',
              onClick: () => setIsEditRemindersDialogOpened(true),
            },
          ],
        },
      ].filter(Boolean) as Array<ResultListItem>);

  return (
    <>
      <RestoreQuestionnaireDialog
        isOpen={isRestoreDialogOpened}
        onClose={() => setIsRestoreDialogOpened(false)}
        selectedQuestionnaires={selectedQuestionnaires}
      />
      <EditTemplateReminderDialog
        isOpen={isSendRemindersDialogOpened}
        onClose={() => setIsSendRemindersDialogOpened(false)}
        selectedQuestionnaires={selectedQuestionnaires}
        reminderEnabledQuestionnairesCount={
          reminderEnabledQuestionnaires.length
        }
        title={`Edit/Send Reminders (${selectedCount})`}
      />

      <EditDueDateDialog
        isOpen={isEditDueDateDialogOpened}
        loadQuestionnaires={loadQuestionnaires}
        onClose={() => setIsEditDueDateDialogOpened(false)}
        selectedQuestionnaires={selectedQuestionnaires}
      />

      <EditRemindersDialog
        isOpen={isEditRemindersDialogOpened}
        onClose={() => {
          loadQuestionnaires();
          setIsEditRemindersDialogOpened(false);
        }}
        selectedQuestionnaires={selectedQuestionnaires}
      />

      <EditTagsDialog
        isOpen={isEditEntityTagsDialogOpened}
        onClose={() => setIsEditEntityTagsDialogOpened(false)}
        selectedEntityIds={uniqueEntityIds}
      />

      <DeleteQuestionnairesDialog
        isOpen={isDeleteQuestionnairesDialogOpened}
        loadQuestionnaires={loadQuestionnaires}
        onClose={() => setIsDeleteQuestionnairesDialogOpened(false)}
        selectedQuestionnaires={selectedQuestionnaires}
        setSelectedQuestionnaires={setSelectedQuestionnaires}
      />

      <SelectFirmInternalStatusDialog
        isOpen={isChangeFirmInternalStatusDialogOpened}
        loadQuestionnaires={loadQuestionnaires}
        onClose={() => setIsChangeFirmInternalStatusDialogOpened(false)}
        selectedQuestionnaires={selectedQuestionnaires}
        title={`Select Firm-Internal Status for (${selectedCount}) questionnaire(s)`}
      />

      <div data-testid="multi-select-actions">
        <Dropdown isDisabled={isLoading} items={items}>
          <Button
            iconLeft="dots-three"
            intent="secondary"
            isIconOnly={true}
            isLoading={isLoading}
            size="small"
            value="More Actions"
            withTooltip={true}
          />
        </Dropdown>
      </div>
    </>
  );
};
